import React from 'react'
import { useSelector } from 'react-redux'
import styled from '@emotion/styled'
import { toMoney } from '@mobi/utils/money'
import type { InvestmentType } from '@mobi/betslip/types'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, radius, spacing } from '@mobi/component-library/Foundations'
import {
  selectBetSlipItemAppliedBonus,
  selectBetSlipItems,
} from '@mobi/betslip/Store/Bets/selectors'
import { selectWorkflowStatus } from '@mobi/betslip/Store/Workflow/selectors'
import { getBetsInMulti } from '@mobi/betslip/helpers/state'
import { MULTI_FORMULA_COMBINATIONS } from '@mobi/betslip/helpers/constants'

const enum LocalConstants {
  BonusCashConfirmState = 'js-bonuscash--confirm',
  BonusCashLowBalance = 'js-bonuscash--lowbalance',
}

export const BonusCashInvestment: React.FC<{
  itemId: string
  value: number
  investmentType: InvestmentType
  finalBonusValue: number
}> = ({ itemId, value, investmentType, finalBonusValue }) => {
  const workflowStatus = useAppSelector(selectWorkflowStatus)
  const items = useAppSelector(selectBetSlipItems)

  let comboCount = 0
  if (itemId === 'f1' || itemId === 'f2' || itemId === 'f2' || itemId === 'f4' || itemId === 'f5') {
    const selectedMultiItems = getBetsInMulti(items)
    const legCount = selectedMultiItems.length

    const combos = MULTI_FORMULA_COMBINATIONS[itemId]
    comboCount = combos[`${legCount}`]
  }

  const isInvestmentComboType =
    itemId === 'f1' || itemId === 'f2' || itemId === 'f3' || itemId === 'f4' || itemId === 'f5'

  const finalBonusValueForItem = useSelector(
    selectBetSlipItemAppliedBonus(itemId, investmentType, finalBonusValue)
  )
  const appliedBonus =
    itemId === 'Multi' || isInvestmentComboType ? finalBonusValue : finalBonusValueForItem

  const hasLowBonusCash = isInvestmentComboType
    ? value * comboCount > appliedBonus && value * comboCount != appliedBonus
    : value != appliedBonus

  if (workflowStatus !== 'ready') {
    return (
      <span>
        {hasLowBonusCash ? (
          <>
            <span style={{ minWidth: '8rem' }}>
              {`${toMoney(value - appliedBonus, { decimalPlaces: 2 })} + `}
            </span>

            <WrapperStyled className={LocalConstants.BonusCashConfirmState}>
              {`${toMoney(hasLowBonusCash ? appliedBonus : value, { decimalPlaces: 2 })} Bonus Cash`}
            </WrapperStyled>
          </>
        ) : (
          <WrapperStyled className={LocalConstants.BonusCashConfirmState}>
            Bonus Cash {`${toMoney(hasLowBonusCash ? appliedBonus : value, { decimalPlaces: 2 })} `}
          </WrapperStyled>
        )}
      </span>
    )
  }

  return (
    <WrapperStyled className={hasLowBonusCash ? LocalConstants.BonusCashLowBalance : ''}>
      {toMoney(value, { decimalPlaces: 2 })}

      <span>
        <Icon name='LineCurrencyDollarCircle' color={colors.celery[600]} size='1.6rem' />
      </span>
    </WrapperStyled>
  )
}

const WrapperStyled = styled.span({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  color: colors.celery[600],
  width: '100%',

  [`&.${LocalConstants.BonusCashConfirmState}`]: {
    color: colors.success[600],
    background: colors.celery[50],
    borderRadius: radius.smx1,
    padding: `0 ${spacing.smx2}`,
  },

  [`&.${LocalConstants.BonusCashLowBalance}`]: {
    color: colors.black,
  },

  // Icon
  '> span': {
    position: 'absolute',
    right: 0,
    marginRight: `-${spacing.smx1}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: colors.success[50],
    width: spacing.bigx1,
    height: spacing.bigx1,
    borderRadius: radius.smx1,
  },
})
